import moment from "moment";
import _ from "lodash";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { getMemberReimbursementDetails } from '../../services';
import Dashreport from "./Dashreport";
import { Chart } from "../../view/home/Chart";
import { useSelector } from "react-redux";

export default function Modals({ Member, toggleProfile, metrics, isAttendance = false }) {
  const members = useSelector((state) => state.members.members);
  const [memberDetails, setMemberDetails] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchMemberDetails = async () => {
    if (!Member?.MemberId) {
      console.log('No MemberId provided');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      console.log('Fetching details for MemberId:', Member.MemberId);

      const response = await getMemberReimbursementDetails(Member.MemberId);
      console.log('API Response:', response);

      if (response?.Result) {
        console.log('Setting member details:', response.Result);
        setMemberDetails(response.Result);
      } else {
        setError('No data received from the server');
        console.error('Invalid response format:', response);
      }
    } catch (error) {
      console.error('Error fetching member details:', error);
      setError('Failed to fetch member details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  const selectedMember = members.find((member) => member.Id === Member.Id);

  useEffect(() => {
    if (Member?.MemberId) {
      console.log('MemberId changed, fetching details...');
      fetchMemberDetails();
    }
  }, [Member?.MemberId]);

  const handleOutsideClick = (e) => {
    if (e.target.id === "modal-overlay") {
      toggleProfile();
    }
  };

  // Debug log for memberDetails
  useEffect(() => {
    console.log('Current memberDetails:', memberDetails);
  }, [memberDetails]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      id="modal-overlay"
      onClick={handleOutsideClick}
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-md p-4 sm:p-6 overflow-y-auto"
    >
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { 
          background: rgba(156, 163, 175, 0.3); 
          border-radius: 10px; 
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(75, 85, 99, 0.3); }
      `}</style>
      
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative bg-white dark:bg-[#0f172a] rounded-[2rem] shadow-2xl w-full max-w-5xl my-auto overflow-hidden border border-white/20 dark:border-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative Background Glows */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-r from-blue-600/20 to-indigo-600/20 blur-3xl -z-10" />
        
        <div className="flex flex-col h-full max-h-[85vh] overflow-y-auto custom-scrollbar p-6 sm:p-10">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8 mb-10">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
              <div className="relative w-32 h-32 rounded-full border-4 border-white dark:border-gray-800 overflow-hidden shadow-xl">
                <img
                  src={Member?.PhotoPath}
                  alt={Member?.MemberName}
                  className="w-full h-full object-cover transform transition duration-500 group-hover:scale-110"
                />
              </div>
              <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 border-4 border-white dark:border-gray-900 rounded-full shadow-lg"></div>
            </div>

            <div className="flex-1 text-center md:text-left pt-2">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-wider mb-3">
                {Member?.Designation || "Employee"}
              </div>
              <h2 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-2 tracking-tight">
                {Member?.MemberName}
              </h2>
                <div className="flex flex-wrap justify-center md:justify-start gap-4 text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800/50 px-3 py-1.5 rounded-lg border border-gray-100 dark:border-gray-700/50">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-medium">{Member?.EmailAddress}</span>
                </div>
                {(Member?.CNIC || Member?.cnic) && (
                  <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800/50 px-3 py-1.5 rounded-lg border border-gray-100 dark:border-gray-700/50">
                    <svg className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4-2v4m0 0V4m0 4h4m-4 0H6" />
                    </svg>
                    <span className="text-sm font-medium font-mono tracking-wide">{Member.CNIC || Member.cnic}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800/50 px-3 py-1.5 rounded-lg border border-gray-100 dark:border-gray-700/50">
                  <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-medium">Joined {Member?.JoiningDate || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="space-y-8">
            
            {/* 1. Attendance Section (Conditional) */}
            {isAttendance && (
              <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 rounded-xl bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Performance Overview</h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-1">
                  <div className="bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 shadow-sm">
                    <Dashreport metrics={metrics} workHours={selectedMember?.workHours} />
                  </div>
                  <div className="bg-white dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 shadow-sm min-h-[300px]">
                    <Chart metrics={metrics} />
                  </div>
                </div>
              </section>
            )}

            {/* 2. Employment & Bank Details Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Employment Sidebar Card */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/80 dark:to-gray-800/80 rounded-[2rem] p-8 border border-white dark:border-gray-700/50 shadow-inner">
                <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                  <span className="w-1.5 h-6 bg-blue-600 rounded-full"></span>
                  Work Schedule
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-1 p-4 rounded-2xl bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-widest">Shift Starts</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {Member?.ShiftStart ? moment.utc(Member.ShiftStart, "HH:mm:ss").local().format("hh:mm A") : "N/A"}
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-widest">Shift Ends</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {Member?.ShiftEnd ? moment.utc(Member.ShiftEnd, "HH:mm:ss").local().format("hh:mm A") : "N/A"}
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-widest">Daily Hours</p>
                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{Member?.workHours || "0"} hrs</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-widest">Tax Rate</p>
                    <p className="text-lg font-bold text-rose-500">{Member?.taxRate || "0"}%</p>
                  </div>
                  {/* <div className="col-span-2 p-4 rounded-2xl bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-widest">Late CheckIn Minutes</p>
                    <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{Member?.LateCheckInMinutes || "0"} mins</p>
                  </div> */}
                  <div className="col-span-2 p-5 rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/20">
                    <p className="text-[10px] font-bold text-white/70 uppercase mb-1 tracking-widest">Monthly Salary</p>
                    <p className="text-2xl font-black">Rs. {Member?.Salary?.toLocaleString() || "0"}</p>
                  </div>
                </div>
              </div>

              {/* Bank Details Card */}
              <div className="bg-white dark:bg-gray-900 p-8 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-6 opacity-5 dark:opacity-10">
                  <svg className="w-24 h-24 text-gray-900 dark:text-white" fill="currentColor" viewBox="0 0 24 24">
                     <path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z"/>
                  </svg>
                </div>
                <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2 relative">
                  <span className="w-1.5 h-6 bg-green-600 rounded-full"></span>
                  Financial Profile
                </h4>
                
                <div className="space-y-6 relative">
                  <div className="group">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest px-1">Bank Name</p>
                    <div className="bg-gray-50 dark:bg-gray-800/40 p-3 rounded-xl border border-transparent group-hover:border-gray-200 dark:group-hover:border-gray-700 transition-all">
                      <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{Member?.BankName || "N/A"}</p>
                    </div>
                  </div>

                  <div className="group">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest px-1">Account Title</p>
                    <div className="bg-gray-50 dark:bg-gray-800/40 p-3 rounded-xl border border-transparent group-hover:border-gray-200 dark:group-hover:border-gray-700 transition-all">
                      <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{Member?.BankAccountTitle || "N/A"}</p>
                    </div>
                  </div>

                  <div className="group">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest px-1">Account Number</p>
                    <div className="bg-gray-50 dark:bg-gray-800/40 p-3 rounded-xl border border-transparent group-hover:border-gray-200 dark:group-hover:border-gray-700 transition-all">
                      <p className="text-sm font-mono font-bold text-green-600 tracking-wider break-all leading-relaxed">
                        {Member?.BankAccountNumber || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="group">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest px-1">Branch</p>
                    <div className="bg-gray-50 dark:bg-gray-800/40 p-3 rounded-xl border border-transparent group-hover:border-gray-200 dark:group-hover:border-gray-700 transition-all">
                      <p className="text-xs font-bold text-gray-600 dark:text-gray-300 leading-normal">
                        {Member?.BankBranchName || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Reimbursement Details Section */}
            <section className="bg-gray-50 dark:bg-gray-900/30 rounded-[2rem] p-8 border border-gray-200/50 dark:border-gray-800">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Medical Allowances</h3>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { title: "OPD", data: Member?.MedicalDetails?.Type1, color: "blue" },
                  { title: "Maternity", data: Member?.MedicalDetails?.Type2, color: "indigo" },
                  { title: "Hospitalization", data: Member?.MedicalDetails?.Type3, color: "purple" }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700/50">
                    <h5 className={`text-sm font-bold uppercase tracking-widest text-${item.color}-500 mb-4`}>{item.title}</h5>
                    <div className="space-y-4">
                      <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`bg-${item.color}-500 h-full rounded-full transition-all duration-1000`}
                          style={{ width: `${Math.min(100, (item.data?.usedAmount / item.data?.totalLimit) * 100) || 0}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400 font-medium">Used: Rs. {item.data?.usedAmount?.toLocaleString() || 0}</span>
                        <span className="text-gray-900 dark:text-white font-bold">Rs. {item.data?.totalLimit?.toLocaleString() || 0}</span>
                      </div>
                      <div className="pt-2 border-t border-gray-50 dark:border-gray-700 flex justify-between items-center">
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Remaining</span>
                        <span className="text-sm font-bold text-green-500">Rs. {item.data?.remaining?.toLocaleString() || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

             
            </section>

          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}